import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";

// Utility function to calculate next signal time
const getNextSignalTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const nextInterval = Math.ceil(minutes / 15) * 15;
  const next = new Date(now);
  next.setMinutes(nextInterval, 0, 0);
  return next;
};

// CountdownTimer Component
const CountdownTimer = ({ nextSignalTime }) => {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const next = new Date(nextSignalTime);
      const diff = next - now;
      
      if (diff > 0) {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setTimeLeft("0:00");
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [nextSignalTime]);

  return (
    <div className="text-4xl font-bold text-center">
      <div className="text-sm text-gray-400">Next Signal In</div>
      <div className="text-indigo-400">{timeLeft}</div>
    </div>
  );
};

// SignalCycleStatus Component
const SignalCycleStatus = () => {
  const [cycleInfo, setCycleInfo] = useState({
    cycleNumber: 147,
    lastSignal: new Date(Date.now() - 5 * 60000).toISOString(),
    agentTesterStatus: "processing",
    agentTradeStatus: "completed"
  });

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="grid grid-cols-4 gap-4 text-center">
        <div>
          <div className="text-sm text-gray-400">Cycle #</div>
          <div className="text-xl font-semibold">{cycleInfo.cycleNumber}</div>
        </div>
        <div>
          <div className="text-sm text-gray-400">Last Signal</div>
          <div className="text-sm">{new Date(cycleInfo.lastSignal).toLocaleTimeString()}</div>
        </div>
        <div>
          <div className="text-sm text-gray-400">AgentTester</div>
          <div className={`text-sm font-medium ${
            cycleInfo.agentTesterStatus === 'completed' ? 'text-green-400' : 'text-yellow-400'
          }`}>
            {cycleInfo.agentTesterStatus}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-400">AgentTrade</div>
          <div className={`text-sm font-medium ${
            cycleInfo.agentTradeStatus === 'completed' ? 'text-green-400' : 'text-yellow-400'
          }`}>
            {cycleInfo.agentTradeStatus}
          </div>
        </div>
      </div>
    </div>
  );
};

// LivePerformancePanel Component
const LivePerformancePanel = ({ system, asset, data }) => {
  // Mini equity curve data
  const equityCurve = [
    { time: '09:00', value: 10000 },
    { time: '09:15', value: 10050 },
    { time: '09:30', value: 10120 },
    { time: '09:45', value: 10080 },
    { time: '10:00', value: 10150 },
    { time: '10:15', value: 10200 },
    { time: '10:30', value: 10250 }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{system}</h3>
        <span className="text-sm bg-gray-700 px-3 py-1 rounded">{asset}</span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-sm text-gray-400">Equity</div>
          <div className="text-2xl font-bold text-green-400">${data.equity}</div>
        </div>
        <div>
          <div className="text-sm text-gray-400">Today's P&L</div>
          <div className={`text-2xl font-bold ${data.todayPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {data.todayPnl >= 0 ? '+' : ''}{data.todayPnl}%
          </div>
        </div>
      </div>

      {/* Mini Equity Chart */}
      <div className="h-24 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={equityCurve}>
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#10b981" 
              strokeWidth={2} 
              dot={false} 
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="text-center">
          <div className="text-gray-400">Wins</div>
          <div className="font-semibold text-green-400">{data.wins}</div>
        </div>
        <div className="text-center">
          <div className="text-gray-400">Losses</div>
          <div className="font-semibold text-red-400">{data.losses}</div>
        </div>
        <div className="text-center">
          <div className="text-gray-400">Total</div>
          <div className="font-semibold">{data.total}</div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-700">
        <div className="text-sm text-gray-400 mb-1">Last Decision</div>
        <div className="flex items-center justify-between">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            data.lastDecision === 'BUY' ? 'bg-green-900 text-green-200' : 
            data.lastDecision === 'SELL' ? 'bg-red-900 text-red-200' : 
            'bg-gray-700 text-gray-300'
          }`}>
            {data.lastDecision}
          </span>
          <span className="text-xs text-gray-400">{data.lastDecisionTime}</span>
        </div>
      </div>
    </div>
  );
};

// ComparisonMetrics Component
const ComparisonMetrics = ({ metrics }) => {
  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Live Comparison</h3>
      <div className="grid grid-cols-4 gap-4">
        <div className="text-center">
          <div className="text-sm text-gray-400">Agreement Rate</div>
          <div className={`text-2xl font-bold ${metrics.agreementRate >= 80 ? 'text-green-400' : 'text-yellow-400'}`}>
            {metrics.agreementRate}%
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-400">Correlation</div>
          <div className="text-2xl font-bold text-blue-400">{metrics.correlation}</div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-400">Divergences</div>
          <div className="text-2xl font-bold text-orange-400">{metrics.divergences}</div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-400">Performance Δ</div>
          <div className={`text-2xl font-bold ${metrics.performanceDelta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {metrics.performanceDelta >= 0 ? '+' : ''}{metrics.performanceDelta}%
          </div>
        </div>
      </div>
      
      {metrics.lastDivergence && (
        <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700 rounded">
          <div className="text-sm text-yellow-400">⚠️ Last Divergence</div>
          <div className="text-xs text-gray-300 mt-1">{metrics.lastDivergence}</div>
        </div>
      )}
    </div>
  );
};

// QuickActionGrid Component
const QuickActionGrid = () => {
  const actions = [
    { label: "Emergency Stop", icon: "🛑", color: "bg-red-600 hover:bg-red-700", action: () => alert("Emergency Stop Activated!") },
    { label: "Send Alert", icon: "📱", color: "bg-blue-600 hover:bg-blue-700", action: () => alert("Sending Telegram alert...") },
    { label: "Refresh All", icon: "🔄", color: "bg-green-600 hover:bg-green-700", action: () => window.location.reload() },
    { label: "Screenshot", icon: "📸", color: "bg-purple-600 hover:bg-purple-700", action: () => alert("Taking screenshot...") },
  ];

  return (
    <div className="grid grid-cols-4 gap-4">
      {actions.map((action, idx) => (
        <button
          key={idx}
          onClick={action.action}
          className={`${action.color} text-white rounded-lg p-4 min-h-[60px] flex flex-col items-center justify-center transition-colors`}
        >
          <span className="text-2xl mb-1">{action.icon}</span>
          <span className="text-xs font-medium">{action.label}</span>
        </button>
      ))}
    </div>
  );
};

// Live Overview Tab
const LiveOverview = () => {
  const [agentTesterData] = useState({
    equity: 10250,
    todayPnl: 2.5,
    wins: 8,
    losses: 3,
    total: 11,
    lastDecision: "BUY",
    lastDecisionTime: "10:45:23"
  });

  const [agentTradeData] = useState({
    equity: 3127,
    todayPnl: -0.8,
    wins: 6,
    losses: 5,
    total: 11,
    lastDecision: "HOLD",
    lastDecisionTime: "10:45:45"
  });

  const [comparisonMetrics] = useState({
    agreementRate: 82,
    correlation: 0.87,
    divergences: 2,
    performanceDelta: 3.3,
    lastDivergence: "Cycle 146: AgentTester BUY vs AgentTrade HOLD on BTC signal"
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-6">
        <SignalCycleStatus />
        <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-center">
          <CountdownTimer nextSignalTime={getNextSignalTime()} />
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <QuickActionGrid />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <LivePerformancePanel 
          system="AgentTester (Live Backtest)" 
          asset="ETH-USDT" 
          data={agentTesterData} 
        />
        <LivePerformancePanel 
          system="AgentTrade (Live Trading)" 
          asset="BTC-USDT" 
          data={agentTradeData} 
        />
      </div>

      <ComparisonMetrics metrics={comparisonMetrics} />
    </div>
  );
};

// Backtesting Suite Components
const BacktestControls = () => {
  const [params, setParams] = useState({
    driverId: "",
    tp: "3",
    sl: "1.5",
    direction: "long"
  });

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Historical Backtest</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Driver ID</label>
          <input
            type="text"
            value={params.driverId}
            onChange={(e) => setParams({...params, driverId: e.target.value})}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            placeholder="BTC-Momentum-v1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Upload CSV</label>
          <input
            type="file"
            accept=".csv"
            className="w-full bg-gray-700 border border-gray-600 rounded p-2"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">TP %</label>
            <input
              type="number"
              value={params.tp}
              onChange={(e) => setParams({...params, tp: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">SL %</label>
            <input
              type="number"
              value={params.sl}
              onChange={(e) => setParams({...params, sl: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Direction</label>
            <select
              value={params.direction}
              onChange={(e) => setParams({...params, direction: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            >
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
          </div>
        </div>
        <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium">
          Run Backtest
        </button>
      </div>
    </div>
  );
};

const BacktestResults = () => {
  const results = [
    { id: 1, driver: "BTC-Momentum-v1", date: "2025-06-03 09:15", pnl: 12.5, trades: 156, winRate: 67 },
    { id: 2, driver: "ETH-Scalper", date: "2025-06-03 08:00", pnl: 8.3, trades: 234, winRate: 58 },
    { id: 3, driver: "SOL-Trend", date: "2025-06-02 16:45", pnl: -3.2, trades: 89, winRate: 42 }
  ];

  const waterfallData = [
    { trade: 'T1', pnl: 2.5 },
    { trade: 'T2', pnl: -1.2 },
    { trade: 'T3', pnl: 3.0 },
    { trade: 'T4', pnl: -0.8 },
    { trade: 'T5', pnl: 4.1 }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Completed Backtests</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2">Driver</th>
                <th className="text-left py-2">Date</th>
                <th className="text-right py-2">P&L</th>
                <th className="text-right py-2">Win%</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => (
                <tr key={r.id} className="border-b border-gray-700/50">
                  <td className="py-2">{r.driver}</td>
                  <td className="py-2 text-gray-400">{r.date}</td>
                  <td className={`py-2 text-right font-medium ${r.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {r.pnl >= 0 ? '+' : ''}{r.pnl}%
                  </td>
                  <td className="py-2 text-right">{r.winRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 p-4 bg-gray-700 rounded">
          <h4 className="font-medium mb-2">Live Backtest Status</h4>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">AgentTester Running</span>
            <span className="px-2 py-1 bg-green-900 text-green-200 rounded text-xs">Active</span>
          </div>
          <div className="mt-2 text-sm text-gray-400">
            Current Cycle: 147 | Trades Today: 11
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4">P&L Waterfall</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={waterfallData}>
              <XAxis dataKey="trade" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                labelStyle={{ color: '#9ca3af' }}
              />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                {waterfallData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const AdvancedBacktestTools = () => {
  return (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-3">Monte Carlo Simulation</h4>
        <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded">
          Run Monte Carlo (1000 trials)
        </button>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-3">Grid Optimization</h4>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input placeholder="TP Min" className="bg-gray-700 rounded px-3 py-2" />
          <input placeholder="TP Max" className="bg-gray-700 rounded px-3 py-2" />
          <input placeholder="SL Min" className="bg-gray-700 rounded px-3 py-2" />
          <input placeholder="SL Max" className="bg-gray-700 rounded px-3 py-2" />
        </div>
        <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded">
          Run Grid Search
        </button>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-3">Hybrid Backtest</h4>
        <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded">
          Run Hybrid Analysis
        </button>
      </div>
    </div>
  );
};

const BacktestingSuite = () => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="space-y-6">
        <BacktestControls />
      </div>
      <div className="space-y-6">
        <BacktestResults />
      </div>
      <div>
        <AdvancedBacktestTools />
      </div>
    </div>
  );
};

// Trading Suite Components
const ActiveDriversPanel = () => {
  const drivers = [
    { id: 1, name: "BTC-Momentum-v1", asset: "BTC-USDT", status: "Active", pnl: 2.3, leverage: "2x" },
    { id: 2, name: "ETH-Scalper", asset: "ETH-USDT", status: "Active", pnl: -0.8, leverage: "3x" },
    { id: 3, name: "SOL-Trend", asset: "SOL-USDT", status: "Paused", pnl: 1.2, leverage: "2x" }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Active Drivers</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm bg-gray-700 px-3 py-1 rounded">3/5 Active</span>
          <span className="text-sm text-gray-400">Wallet: 3.127 ETH</span>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-2">Driver</th>
              <th className="text-left py-2">Asset</th>
              <th className="text-left py-2">Leverage</th>
              <th className="text-left py-2">Status</th>
              <th className="text-right py-2">P&L</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(d => (
              <tr key={d.id} className="border-b border-gray-700/50">
                <td className="py-2">{d.name}</td>
                <td className="py-2">{d.asset}</td>
                <td className="py-2">{d.leverage}</td>
                <td className="py-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    d.status === 'Active' ? 'bg-green-900 text-green-200' : 'bg-yellow-900 text-yellow-200'
                  }`}>
                    {d.status}
                  </span>
                </td>
                <td className={`py-2 text-right font-medium ${d.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {d.pnl >= 0 ? '+' : ''}{d.pnl}%
                </td>
                <td className="py-2 text-right">
                  <button className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs mr-2">
                    Pause
                  </button>
                  <button className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs">
                    Stop
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PositionsPanel = () => {
  const positions = [
    { id: 1, symbol: "BTC-PERP", size: 0.02, entry: 42000, current: 42350, pnl: 125, tp: 43000, sl: 41500 },
    { id: 2, symbol: "ETH-PERP", size: 0.5, entry: 3100, current: 3085, pnl: -37.5, tp: 3150, sl: 3080 }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Open Positions</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-2">Symbol</th>
              <th className="text-right py-2">Size</th>
              <th className="text-right py-2">Entry</th>
              <th className="text-right py-2">Current</th>
              <th className="text-right py-2">P&L</th>
              <th className="text-right py-2">TP/SL</th>
              <th className="text-right py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {positions.map(p => (
              <tr key={p.id} className="border-b border-gray-700/50">
                <td className="py-2">{p.symbol}</td>
                <td className="py-2 text-right">{p.size}</td>
                <td className="py-2 text-right">${p.entry}</td>
                <td className="py-2 text-right">${p.current}</td>
                <td className={`py-2 text-right font-medium ${p.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {p.pnl >= 0 ? '+' : ''}${p.pnl}
                </td>
                <td className="py-2 text-right text-gray-400">
                  {p.tp}/{p.sl}
                </td>
                <td className="py-2 text-right">
                  <button className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs">
                    Close
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExecutionMonitor = () => {
  const executions = [
    { id: 1, time: "10:45:45", symbol: "BTC-PERP", side: "BUY", size: 0.02, price: 42350, slippage: 0.02, gas: 0.003 },
    { id: 2, time: "10:30:12", symbol: "ETH-PERP", side: "SELL", size: 0.5, price: 3085, slippage: 0.03, gas: 0.004 }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Recent Executions</h3>
      <div className="space-y-3">
        {executions.map(e => (
          <div key={e.id} className="flex justify-between items-center p-3 bg-gray-700 rounded">
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-400">{e.time}</span>
              <span className="font-medium">{e.symbol}</span>
              <span className={`px-2 py-1 rounded text-xs ${
                e.side === 'BUY' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'
              }`}>
                {e.side}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span>Size: {e.size}</span>
              <span>@ ${e.price}</span>
              <span className="text-gray-400">Slip: {e.slippage}%</span>
              <span className="text-gray-400">Gas: {e.gas} ETH</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PortfolioPnLChart = () => {
  const data = [
    { date: 'Mon', pnl: 120 },
    { date: 'Tue', pnl: 180 },
    { date: 'Wed', pnl: 150 },
    { date: 'Thu', pnl: 220 },
    { date: 'Fri', pnl: 280 },
    { date: 'Sat', pnl: 250 },
    { date: 'Sun', pnl: 320 }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Portfolio P&L</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
              labelStyle={{ color: '#9ca3af' }}
            />
            <Line 
              type="monotone" 
              dataKey="pnl" 
              stroke="#10b981" 
              strokeWidth={2}
              dot={{ fill: '#10b981', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const TradingSuite = () => {
  return (
    <div className="space-y-6">
      <ActiveDriversPanel />
      
      <div className="grid grid-cols-2 gap-6">
        <PositionsPanel />
        <ExecutionMonitor />
      </div>
      
      <PortfolioPnLChart />
    </div>
  );
};

// Drivers & Config Components
const DriversConfig = () => {
  const readyDrivers = [
    { id: 1, name: "BTC-Momentum-v1", winRate: 67, trades: 156, confidence: 0.82, status: "Ready" },
    { id: 2, name: "ETH-Scalper", winRate: 58, trades: 234, confidence: 0.65, status: "Ready" },
    { id: 3, name: "SOL-Trend", winRate: 42, trades: 89, confidence: 0.38, status: "Cool-off" }
  ];

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Driver Management */}
      <div className="space-y-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Ready Drivers</h3>
          <div className="space-y-3">
            {readyDrivers.map(d => (
              <div key={d.id} className="p-4 bg-gray-700 rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{d.name}</h4>
                    <div className="mt-1 text-sm text-gray-400">
                      Win Rate: {d.winRate}% | Trades: {d.trades}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`px-2 py-1 rounded text-xs ${
                      d.status === 'Ready' ? 'bg-green-900 text-green-200' : 'bg-yellow-900 text-yellow-200'
                    }`}>
                      {d.status}
                    </div>
                    <div className="mt-1 text-sm">
                      Conf: {(d.confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
                {d.status === 'Ready' && (
                  <button className="mt-3 w-full px-3 py-1 bg-indigo-600 hover:bg-indigo-700 rounded text-sm">
                    Deploy Driver
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Performance Thresholds</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Max Drawdown %</label>
              <input type="number" defaultValue="5" className="w-full bg-gray-700 rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Win Rate %</label>
              <input type="number" defaultValue="60" className="w-full bg-gray-700 rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cool-off Period (hours)</label>
              <input type="number" defaultValue="24" className="w-full bg-gray-700 rounded px-3 py-2" />
            </div>
            <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded">
              Update Thresholds
            </button>
          </div>
        </div>
      </div>
      
      {/* Configuration */}
      <div className="space-y-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Asset Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">AgentTester Asset</label>
              <select className="w-full bg-gray-700 rounded px-3 py-2">
                <option>ETH-USDT</option>
                <option>BTC-USDT</option>
                <option>SOL-USDT</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">AgentTrade Asset</label>
              <select className="w-full bg-gray-700 rounded px-3 py-2">
                <option>BTC-USDT</option>
                <option>ETH-USDT</option>
                <option>SOL-USDT</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">API Configuration</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">n8n Base URL</label>
              <input type="text" defaultValue="http://localhost:5678" className="w-full bg-gray-700 rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">API Key</label>
              <div className="flex gap-2">
                <input type="password" defaultValue="YOUR_SECRET_KEY" className="flex-1 bg-gray-700 rounded px-3 py-2" />
                <button className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded">Show</button>
              </div>
            </div>
            <button className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded">
              Test Connection
            </button>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Telegram Integration</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Bot Token</label>
              <input type="password" placeholder="Your bot token" className="w-full bg-gray-700 rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Chat ID</label>
              <input type="text" placeholder="Your chat ID" className="w-full bg-gray-700 rounded px-3 py-2" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="alerts" className="w-4 h-4" />
              <label htmlFor="alerts" className="text-sm">Enable automatic alerts</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Analytics & Reports Components
const PerformanceComparison = () => {
  const data = [
    { date: 'Mon', agentTester: 150, agentTrade: 120 },
    { date: 'Tue', agentTester: 200, agentTrade: 180 },
    { date: 'Wed', agentTester: 180, agentTrade: 150 },
    { date: 'Thu', agentTester: 250, agentTrade: 220 },
    { date: 'Fri', agentTester: 320, agentTrade: 280 },
    { date: 'Sat', agentTester: 300, agentTrade: 250 },
    { date: 'Sun', agentTester: 380, agentTrade: 320 }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Performance Comparison</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
              labelStyle={{ color: '#9ca3af' }}
            />
            <Line 
              type="monotone" 
              dataKey="agentTester" 
              stroke="#10b981" 
              strokeWidth={2}
              name="AgentTester"
            />
            <Line 
              type="monotone" 
              dataKey="agentTrade" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="AgentTrade"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const TradeHeatmap = () => {
  const hours = ['00', '02', '04', '06', '08', '10', '12', '14', '16', '18', '20', '22'];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Generate random heatmap data
  const generateHeatmapData = () => {
    return days.map(day => 
      hours.map(hour => ({
        day,
        hour,
        value: Math.random() * 100
      }))
    ).flat();
  };

  const heatmapData = generateHeatmapData();
  
  const getColor = (value) => {
    if (value > 80) return 'bg-green-600';
    if (value > 60) return 'bg-green-500';
    if (value > 40) return 'bg-yellow-500';
    if (value > 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Trade Analysis Heatmap</h3>
      <div className="overflow-x-auto">
        <div className="grid grid-cols-13 gap-1 text-xs">
          <div></div>
          {hours.map(h => (
            <div key={h} className="text-center text-gray-400">{h}</div>
          ))}
          {days.map(day => (
            <>
              <div key={day} className="text-gray-400 pr-2">{day}</div>
              {hours.map(hour => {
                const cell = heatmapData.find(d => d.day === day && d.hour === hour);
                return (
                  <div
                    key={`${day}-${hour}`}
                    className={`h-6 rounded ${getColor(cell?.value || 0)}`}
                    title={`${day} ${hour}:00 - ${cell?.value.toFixed(0)}%`}
                  />
                );
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

const AnalyticsReports = () => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <PerformanceComparison />
        <TradeHeatmap />
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Risk Metrics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Sharpe Ratio</span>
              <span className="font-medium">1.82</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Max Drawdown</span>
              <span className="font-medium text-red-400">-8.3%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">VaR (95%)</span>
              <span className="font-medium">-2.1%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Sortino Ratio</span>
              <span className="font-medium">2.14</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Calmar Ratio</span>
              <span className="font-medium">3.65</span>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Export Options</h3>
          <div className="space-y-3">
            <button className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded flex items-center justify-center gap-2">
              <span>📊</span> Export to Google Sheets
            </button>
            <button className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center gap-2">
              <span>📄</span> Download CSV Report
            </button>
            <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded flex items-center justify-center gap-2">
              <span>📸</span> Screenshot Dashboard
            </button>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Telegram Reports</h3>
          <div className="space-y-3">
            <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded">
              Send Daily Summary
            </button>
            <button className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded">
              Send Performance Alert
            </button>
            <div className="text-sm text-gray-400 text-center">
              Last sent: 2 hours ago
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
export default function App() {
  const [currentTab, setCurrentTab] = useState("live");

  const tabs = [
    { id: "live", label: "Live Overview", icon: "🔴" },
    { id: "backtest", label: "Backtesting Suite", icon: "📊" },
    { id: "trading", label: "Trading Suite", icon: "💰" },
    { id: "drivers", label: "Drivers & Config", icon: "⚙️" },
    { id: "analytics", label: "Analytics & Reports", icon: "📈" }
  ];

  // Auto-refresh every 30 seconds after signal time
  useEffect(() => {
    const checkAndRefresh = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      
      // Refresh 30 seconds after each 15-minute interval
      if ((minutes % 15 === 0 && seconds === 30) || 
          (minutes % 15 === 1 && seconds === 0)) {
        window.location.reload();
      }
    };

    const interval = setInterval(checkAndRefresh, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">AgentTester / AgentTrade Dashboard</h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-400">Last update: {new Date().toLocaleTimeString()}</span>
              <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm">
                Settings
              </button>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <nav className="mt-4 flex gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setCurrentTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  currentTab === tab.id
                    ? 'bg-gray-700 text-white'
                    : 'hover:bg-gray-700/50 text-gray-400'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        {currentTab === "live" && <LiveOverview />}
        {currentTab === "backtest" && <BacktestingSuite />}
        {currentTab === "trading" && <TradingSuite />}
        {currentTab === "drivers" && <DriversConfig />}
        {currentTab === "analytics" && <AnalyticsReports />}
      </main>

      {/* Hidden iframe for always-on mode */}
      <iframe 
        title="keep-alive" 
        src="about:blank" 
        style={{ display: 'none' }}
        onLoad={(e) => {
          // Prevent screen timeout
          if (e.target.contentWindow) {
            e.target.contentWindow.document.body.style.backgroundColor = '#000';
          }
        }}
      />
    </div>
  );
}
